// +----------------------------------------------------------------------
// | iOS Safari「添加到主屏幕」强制引导（只拦首页）
// | 命中条件（全部满足）才渲染，且不给关闭入口 —— 只有真的添加了才消失：
// |   ① 当前页是首页 /（/vip、/free-code 这类付费/领码路径绝不拦，blocker 掐转化）
// |   ② iOS（含 iPadOS 13+，它的 Safari 伪装成 Mac，靠触点数识别）
// |   ③ Safari 本体（微信/Chrome 等壳没有「添加到主屏幕」，引导了是误导）
// |   ④ 非独立模式（已从桌面图标打开 = navigator.standalone → 永不弹）
// | 例外：URL 带 ?mb=1 跳过（本会话内不再弹），给投放链接留的后门。
// +----------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from '../Toast';
import './IosGuide.less';

const SKIP_KEY = 'xx-pwa-mb-skip'; // ?mb=1 设的会话级跳过标记

function isIos() {
  const ua = navigator.userAgent || '';
  const iDevice = /iPhone|iPad|iPod/i.test(ua);
  // iPadOS 13+ 的 Safari UA 是 Macintosh，但触点数 > 1（真 Mac 没有触摸屏）
  const iPadOs = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return iDevice || iPadOs;
}

function isSafariBrowser() {
  const ua = navigator.userAgent || '';
  // 这些壳里没有「添加到主屏幕」：微信、Safari 的各家马甲、国产浏览器
  if (/MicroMessenger|CriOS|FxiOS|EdgiOS|UCBrowser|QQBrowser|Quark|Baidu|Sogou|360/i.test(ua)) {
    return false;
  }
  // Safari 判定：UA 里既有 Safari 又有 Version/（套壳浏览器只有前者）
  return /Safari/i.test(ua) && /Version\//i.test(ua);
}

function isStandalone() {
  return (
    window.navigator.standalone === true ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
}

export default function IosGuide() {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();
  const shownRef = useRef(false); // 本次会话只拦一次（拦住后用户也走不了，防重复触发）

  // 只在首页 / 判断要不要拦人，并且等到真正停在首页那一刻（可能是跳过来的）
  useEffect(() => {
    if (shownRef.current) return undefined;

    // ?mb=1 先处理：哪怕这次落在子页，也先把本会话的跳过标记立上
    try {
      if (new URLSearchParams(window.location.search).get('mb') === '1') {
        sessionStorage.setItem(SKIP_KEY, '1');
      }
      if (sessionStorage.getItem(SKIP_KEY) === '1') return undefined;
    } catch (e) {
      // 隐私模式下 sessionStorage 可能不可用：不跳过，继续按设备条件判断
    }

    if (pathname !== '/') return undefined;

    if (!isIos() || !isSafariBrowser() || isStandalone()) return undefined;

    // 稍等一下再拦，让人先看清是什么页面，别一上来糊脸
    const t = setTimeout(() => {
      shownRef.current = true;
      setShow(true);
    }, 900);
    return () => clearTimeout(t);
  }, [pathname]);

  // 弹层在时必须锁滚动，否则背后的页面会跟着手指滚
  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  // 用户去分享面板加完、切回前台时复查：真加成了（独立模式）就自动收摊
  useEffect(() => {
    if (!show) return undefined;
    const recheck = () => {
      if (isStandalone()) setShow(false);
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('pageshow', recheck);
    window.addEventListener('focus', recheck);
    return () => {
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('pageshow', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, [show]);

  // 「我添加好了」：Safari 不告诉网页用户有没有真的点「添加到主屏幕」，
  // 只能复查独立模式；没检测到就提示重来（不关弹层，保持强制）。
  const handleAdded = useCallback(() => {
    if (isStandalone()) {
      setShow(false);
      return;
    }
    toast('还没添加成功～按上面 3 步操作一下');
  }, []);

  if (!show) return null;

  return (
    <div className="ios-guide" role="dialog" aria-modal="true" aria-label="添加到主屏幕引导">
      <div className="ios-guide-bg" />

      <div className="ios-guide-body">
        <i className="ios-guide-logo">想</i>
        <h2 className="ios-guide-title">把「想想」装到桌面</h2>
        <p className="ios-guide-sub">像 App 一样全屏打开，不用每次翻网址</p>

        <ol className="ios-guide-steps">
          <li>
            <em>1</em>
            <span>
              点 Safari 底部中间的
              <i className="ios-guide-sharekey">
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="4.5" y="9" width="15" height="12" rx="2.2" />
                  <path d="M12 15V3.5M8.2 7 12 3.2 15.8 7" />
                </svg>
              </i>
              分享键
            </span>
          </li>
          <li>
            <em>2</em>
            <span>在弹出的列表里往下滑，选「添加到主屏幕」</span>
          </li>
          <li>
            <em>3</em>
            <span>回桌面点「想想」图标打开，就是全屏体验</span>
          </li>
        </ol>

        <button
          type="button"
          className="btn btn-primary btn-block ios-guide-btn"
          onClick={handleAdded}
        >
          我添加好了
        </button>
        <p className="ios-guide-tip">添加完成后，这个提示就再也不会出现</p>
      </div>
    </div>
  );
}
